import { authManager } from '@/shared/auth-manager';
import { eventBus } from '@/shared/event-bus';
import type { ICockpitStore } from '@/store';
import { CondoUiEvents, type ICockpitConfig } from '@/types';
import type { IUser } from '@condo/domain';
import type { AxiosError } from 'axios';
import { debounce } from 'lodash-es';
import type { App } from 'vue';
import VueGoogleMaps from 'vue-google-maps-community-fork';
import { useToast } from 'vue-toastification';
import VueClipboard from 'vue3-clipboard';
import type { Store } from 'vuex';
import { registerCondoSharedComponents } from './ui';
import { UnleashClient, plugin as unleashPlugin } from '@unleash/proxy-client-vue';

const toast = useToast();

export const bootstrapComponents = (app: App) => {
    registerCondoSharedComponents(app);
};

export const getUserDetails = async (store: Store<ICockpitStore>, userObj?: IUser): Promise<IUser> => {
    const user: IUser = userObj || (await store.dispatch('getUserDetails'));

    eventBus.on(CondoUiEvents.Logout, () => {
        authManager.logOut();
        location.reload();
    });
    return user;
};

export const registerThirdPartyComponents = (app: App, config: ICockpitConfig) => {
    app.use(VueClipboard, {
        autoSetContainer: true,
        appendToBody: true,
    });

    app.use(VueGoogleMaps, { load: { key: config.GOOGLE_MAPS_API_KEY, libraries: ['places'] } });
};

export const configureAppInstance = (app: App) => {
    /**
     * Error Handling,
     * For now we just display any unhandled error to the end user,
     * When we have logging for front-end, we can fire a request from here
     */
    app.config.errorHandler = err => {
        const toastFun = debounce(toast.error, 1000);

        console.error(err);
        const axiosErr = err as AxiosError<any>;
        // We ignore axios errors because they are already handled by `showErrorMessage` in `api-client.ts`
        if (axiosErr?.name === 'AxiosError') {
            return;
        }
        toastFun("Oops! Something's not good. Call your development team!", {
            timeout: 10000,
        });
    };

    app.config.compilerOptions.whitespace = 'preserve';
};

export const registerUnleashPlugin = (
    app: App,
    configObj: {
        SERVICE_NAME: string;
        UNLEASH_API_TOKEN?: string;
        UNLEASH_API_URL?: string;
    },
) => {
    if (configObj.UNLEASH_API_TOKEN && configObj.UNLEASH_API_URL) {
        const client = new UnleashClient({
            url: configObj.UNLEASH_API_URL,
            clientKey: configObj.UNLEASH_API_TOKEN,
            refreshInterval: 15,
            appName: configObj.SERVICE_NAME,
        });

        app.use(unleashPlugin, { unleashClient: client });
    }
};
